import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnumOptionLabel } from '../../shared/i18n/enum-labels.js';
import { FIELD_TYPE_OPTIONS, OPTION_FIELD_TYPES, normalizeFields } from './kyc-shared.js';

function KycStepFieldsEditor({
  fields,
  newField,
  isSaving,
  dragSourceLocalId,
  onUpdateField,
  onToggleMandatory,
  onToggleImmutable,
  onToggleVisible,
  onFieldBlur,
  onDuplicateField,
  onRemoveField,
  onFieldDragStart,
  onFieldDragEnd,
  onFieldDrop,
  onOpenOptions,
  onOpenDependency,
  onChangeNewField,
  onAddField,
}) {
  const { t } = useTranslation();
  const [hoveredLocalId, setHoveredLocalId] = useState('');

  return (
    <div>
      <div className="d-flex flex-column gap-2">
        {fields.map((field) => (
          <div
            data-dnd-row="true"
            className={`row g-2 align-items-center px-2 py-1 rounded ${
              dragSourceLocalId === field.localId ? 'opacity-50' : ''
            } ${
              hoveredLocalId === field.localId ? 'bg-light' : ''
            }`}
            key={field.localId}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => onFieldDrop(field.localId)}
            onMouseEnter={() => setHoveredLocalId(field.localId)}
            onMouseLeave={() => setHoveredLocalId('')}
          >
            <div className="col-auto text-nowrap">
              <span
                className={`text-muted ${isSaving ? 'opacity-50' : ''}`}
                draggable={!isSaving}
                onDragStart={(event) => onFieldDragStart(
                  event,
                  field.localId,
                  event.currentTarget.closest('[data-dnd-row="true"]'),
                )}
                onDragEnd={onFieldDragEnd}
                title={t('kyc.dragToReorder')}
              >
                <i className="fa fa-grip-vertical"></i>
              </span>
            </div>
            <div className="col">
              <input
                className="form-control form-control-sm"
                value={field.name}
                onChange={(event) => onUpdateField(field.localId, 'name', event.target.value)}
                onBlur={() => onFieldBlur(field.localId)}
                disabled={isSaving}
              />
            </div>
            <div className="col-auto">
              <select
                className="form-select form-select-sm"
                value={field.type}
                onChange={(event) => onUpdateField(field.localId, 'type', event.target.value)}
                onBlur={() => onFieldBlur(field.localId)}
                disabled={isSaving}
                style={{ width: '140px' }}
              >
                {FIELD_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{getEnumOptionLabel(t, 'kycFieldType', type)}</option>
                ))}
              </select>
            </div>
            <div className="col-auto d-flex justify-content-center">
              <div className="form-check form-switch mt-1 mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(field.required)}
                  onChange={(event) => onToggleMandatory(field.localId, event.target.checked)}
                  disabled={isSaving}
                  title={t('kyc.mandatoryField')}
                />
              </div>
            </div>
            <div className="col-auto d-flex align-items-center justify-content-center">
              <button
                type="button"
                className={`btn btn-sm app-icon-toggle ${field.immutable ? 'is-active' : ''}`}
                onClick={() => onToggleImmutable(field.localId, field.immutable)}
                disabled={isSaving}
                title={t('kyc.immutableField')}
              >
                <i className="fa fa-lock"></i>
              </button>
            </div>
            <div className="col-auto d-flex align-items-center justify-content-center">
              <button
                type="button"
                className={`btn btn-sm app-icon-toggle ${field.visible !== false ? 'is-active' : ''}`}
                onClick={() => onToggleVisible(field.localId, field.visible !== false)}
                disabled={isSaving}
                title={field.visible !== false ? t('kyc.visibleField') : t('kyc.invisibleField')}
              >
                <i className={`fa ${field.visible !== false ? 'fa-eye' : 'fa-eye-slash'}`}></i>
              </button>
            </div>
            <div className="col-auto text-nowrap">
              <button
                type="button"
                className="btn btn-sm me-1 app-icon-toggle"
                onClick={() => onOpenOptions(field.localId)}
                disabled={isSaving || !OPTION_FIELD_TYPES.has(field.type)}
                title={t('kyc.optionsButton')}
              >
                <i className="fa fa-list-ul"></i>
              </button>
              <button
                type="button"
                className={`btn btn-sm me-1 app-icon-toggle ${
                  !isSaving && fields.length >= 2 && normalizeFields(field.dependencies).length > 0
                    ? 'is-active'
                    : ''
                }`}
                onClick={() => onOpenDependency(field.localId)}
                disabled={isSaving || fields.length < 2}
                title={t('kyc.displayConditions')}
              >
                <i className="fa fa-link"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-default me-1"
                onClick={() => onDuplicateField(field.localId)}
                disabled={isSaving}
                title={t('kyc.duplicateField')}
              >
                <i className="fa fa-copy"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => onRemoveField(field.localId)}
                disabled={isSaving}
                title={t('kyc.deleteField')}
              >
                <i className="fa fa-trash"></i>
              </button>
            </div>
          </div>
        ))}

        <div className="row g-2 align-items-center pt-2">
          <div className="col">
            <input
              className="form-control form-control-sm"
              placeholder={t('kyc.newFieldPlaceholder')}
              value={newField.name}
              onChange={(event) => onChangeNewField('name', event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onAddField();
                }
              }}
              disabled={isSaving}
            />
          </div>
          <div className="col-auto">
            <select
              className="form-select form-select-sm"
              value={newField.type}
              onChange={(event) => onChangeNewField('type', event.target.value)}
              disabled={isSaving}
              style={{ width: '140px' }}
            >
              {FIELD_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>{getEnumOptionLabel(t, 'kycFieldType', type)}</option>
              ))}
            </select>
          </div>
          <div className="col-auto"></div>
          <div className="col-auto">
            <button type="button" className="btn btn-sm btn-theme" onClick={onAddField} disabled={isSaving}>
              <i className="fa fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KycStepFieldsEditor;
